import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api/client';
import type { ActivityRun, TenantActivity, TenantLeg, TenantsResponse } from '../api/types';
import { BoardToolbar, ChartGrid } from '../components/ChartBoard';
import { ScreenLink } from '../components/ScreenLink';
import { ago } from '../lib/format';
import { can, useMe } from '../lib/me';
import { useChartBoard } from '../lib/useChartBoard';
import { readParams, withParam, writeUrl } from '../lib/urlState';
import { useLoad } from '../lib/useLoad';
import { LoadIssue } from '../components/LoadIssue';

/**
 * One row per tenant, so "is it them or is it everyone?" is one glance — and,
 * underneath, the same numbers over time, for the fleet or for one tenant.
 *
 * THE DASH IS THE MOST IMPORTANT THING ON THIS SCREEN. Every activity number is
 * nullable, and null means the check is not currently observing — it went
 * unknown, or the front end never tagged an event with this tenant. Rendering
 * that as `0` would say "nothing happened" where the truth is "we are not
 * looking", which is the one substitution this whole service exists to refuse.
 * So: `—` with the reason in the tooltip, never a zero.
 *
 * The numbers are what each check last OBSERVED, persisted by the same commit
 * that feeds the charts — so a deploy no longer blanks the table, and a run that
 * went blind no longer leaves old numbers looking current. Each half of a row
 * (dispatch's audit log, PostHog) carries its own age: a figure the latest run
 * did not observe is drawn grey with when it was observed and why it is not
 * current, and the header names each leg's last read — with the button beside
 * it for somebody who does not want to wait out the interval.
 *
 * The charts live here rather than on Vitals because a per-tenant series is only
 * legible next to the row it belongs to. Picking a tenant costs NOTHING: every
 * panel response already carries one series per tenant, so the picker filters
 * data already in hand. It deliberately does not narrow the request — there is
 * no tenant parameter on `/charts/:panel`, and there must not be one, because
 * that endpoint is a frozen allowlist rather than a PromQL proxy.
 */

/**
 * THE ONE RULE ON THIS SCREEN, extracted so it can be asserted rather than
 * eyeballed: absent and zero are different, and only one of them is a number.
 *
 * `null` and `undefined` mean the check is not reporting that figure — it went
 * unknown, or the front end never tagged an event with this tenant. A literal 0
 * is an observation and renders as one. Getting this backwards would put a
 * confident "0 bookings" against a tenant nobody is looking at, which is the
 * substitution this whole service exists to refuse.
 *
 * Exported for the test beside this file, in the same shape as contentRect in
 * AssistStage.tsx: the decision is a pure function and the component only draws
 * it.
 */
export function activityCell(
  value: number | null | undefined,
  suffix = '',
): { absent: true } | { absent: false; text: string } {
  // Number.isFinite rejects NaN as well, which is what a divide-by-zero
  // upstream would arrive as — and "NaN" drawn in a table is worse than a dash.
  if (typeof value !== 'number' || !Number.isFinite(value)) return { absent: true };
  return { absent: false, text: `${value.toLocaleString()}${suffix}` };
}

/**
 * Past this, a quiet tenant is drawn in the warning colour. It is DSP-03's own
 * silence window, shared on purpose: a column and an incident disagreeing about
 * when something became worrying is how a screen stops being believed.
 */
export const SILENCE_HOURS = 3;

/** Minutes since the last audit row, read as a duration rather than a count. */
export function quietCell(
  minutes: number | null | undefined,
): { absent: true } | { absent: false; text: string; long: boolean } {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return { absent: true };
  if (minutes < 90) return { absent: false, text: `${minutes}m ago`, long: false };
  const hours = Math.floor(minutes / 60);
  const text = hours < 48 ? `${hours}h ago` : `${Math.floor(hours / 24)}d ago`;
  return { absent: false, text, long: hours >= SILENCE_HOURS };
}

/**
 * The panels drawn under the table, and the fetch list.
 *
 * It is a module constant, and it does NOT change with the picker, which is the
 * property the whole design rests on: `useChartBoard` refetches when its key
 * list changes, so a list that narrowed with the selection would turn each click
 * into seven range queries against the Prometheus that also serves Grafana and
 * the alerting rules. `scripts/verify/charts.js` asserts the constant reaches
 * the hook unchanged.
 */
export const TENANT_PANELS = [
  'tenant_bookings',
  'tenant_operator',
  'tenant_quiet',
  'tenant_active_minutes',
  'tenant_users',
  'tenant_fe_errors',
  'tenant_attribution',
];

/**
 * Fleet-wide panels — drawn for everybody, hidden for one.
 *
 * `tenant_attribution` is a single percentage across the whole fleet, legended
 * with the literal `attributed` by `services/promClient.js`, so filtering it to
 * one tenant leaves an empty card that looks like a dead check rather than a
 * question it was never asked. It stays fetched (see above) and is simply not
 * drawn while a tenant is picked.
 */
export const FLEET_PANELS = ['tenant_attribution'];

/** Which of the fetched panels to draw. Pure, so it can be asserted. */
export function panelsFor(selected: string | null): string[] {
  if (!selected) return TENANT_PANELS;
  return TENANT_PANELS.filter((key) => !FLEET_PANELS.includes(key));
}

/**
 * Keep one tenant's series. EXACT match, and that is load-bearing twice over:
 * `watch_metric{tenant="acme"}` is legended with the tenant key verbatim, and
 * the UNATTRIBUTED bucket — events the front end never tagged — comes back
 * under Prometheus's `__name__` fallback, `watch_metric`. A loose match would
 * fold that bucket into whichever tenant it happened to resemble.
 */
export function matchesTenant(selected: string | null): ((label: string) => boolean) | undefined {
  if (!selected) return undefined;
  return (label: string) => label === selected;
}

/**
 * Why a figure is not current, for its tooltip — or null when it is current.
 *
 * Pure, so the rule is asserted rather than eyeballed: a leg is current only
 * when its latest run observed this tenant. A figure from an earlier run is
 * still worth showing (a blind run must not blank the table), but never in the
 * same ink as one from now.
 */
export function staleNote(leg: TenantLeg | null | undefined): string | null {
  if (!leg || leg.fresh) return null;
  const when = leg.observedAt ? `as of ${ago(leg.observedAt)}` : 'never observed';
  return leg.reason ? `${when} — ${leg.reason}` : when;
}

/** A number, or the dash that means we did not observe. Never a zero stand-in. */
function Value({
  value,
  suffix,
  title,
  leg,
}: {
  value: number | null;
  suffix?: string;
  title?: string;
  leg?: TenantLeg;
}) {
  const cell = activityCell(value, suffix);
  const stale = staleNote(leg);
  if (cell.absent) {
    return (
      <span className="wt-dim" title={stale ?? title ?? 'No current observation — this check is not reporting'}>
        —
      </span>
    );
  }
  if (stale) {
    return (
      <span className="wt-stale" title={`Not current, ${stale}`}>
        {cell.text}
      </span>
    );
  }
  return <span>{cell.text}</span>;
}

function Quiet({ minutes, leg }: { minutes: number | null; leg?: TenantLeg }) {
  const cell = quietCell(minutes);
  if (cell.absent) return <Value value={null} title="No audit row has been observed for this tenant" leg={leg} />;
  const stale = staleNote(leg);
  if (stale) {
    return (
      <span className="wt-stale" title={`Not current, ${stale}`}>
        {cell.text}
      </span>
    );
  }
  // wt-quiet-long, not wt-warn: the latter has no rule in ui.css, so the amber
  // silently never applied.
  return <span className={cell.long ? 'wt-quiet-long' : undefined}>{cell.text}</span>;
}

const LEG_NAMES: Record<ActivityRun['leg'], string> = {
  dispatch: 'dispatch activity',
  frontend: 'front-end activity (PostHog)',
};

/**
 * A leg whose latest run observed nothing, or that has stopped running, says
 * so above the table — once for the column, rather than in forty tooltips.
 */
function LegBanner({ checkId, run }: { checkId: string; run: ActivityRun }) {
  const name = LEG_NAMES[run.leg];
  const toLog = (
    <ScreenLink
      to="/systems/logs"
      previewTab="logs"
      search={`q=${encodeURIComponent(checkId)}`}
      className="wt-linkish"
    >
      See its log lines
    </ScreenLink>
  );
  if (run.overdue) {
    return (
      <div className="wt-banner wt-banner-warn">
        The {name} check has not run since {ago(run.lastRunAt)}, so every figure in its columns is from then
        and is drawn grey. {toLog}
      </div>
    );
  }
  if (run.failure?.status === 'skipped') {
    // Not a failure: switched off, and the reason names what to set.
    return (
      <div className="wt-banner wt-banner-warn">
        The {name} check is not reading anything — {run.failure.summary || 'it is skipped'}.{' '}
        {run.lastObservedAt ? `Its figures are from ${ago(run.lastObservedAt)} and are drawn grey. ` : ''}
        {toLog}
      </div>
    );
  }
  if (run.failure) {
    return (
      <div className="wt-banner wt-banner-warn">
        The latest {name} read, {ago(run.lastRunAt)}, observed nothing — {run.failure.summary || run.failure.status}
        . Its figures are from {run.lastObservedAt ? ago(run.lastObservedAt) : 'no earlier read'} and are drawn
        grey. {toLog}
      </div>
    );
  }
  return null;
}

export function Tenants() {
  const { data, error, loading, updatedAt, reload } = useLoad(() => api.get<TenantsResponse>('/tenants'), [], 30_000);
  const board = useChartBoard(TENANT_PANELS);
  const [forcing, setForcing] = useState(false);
  const canRun = can(useMe(), 'checks.run');
  const [note, setNote] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(() => readParams().get('tenant'));

  const pick = (next: string | null) => {
    setPicked(next);
    writeUrl(withParam(readParams(), 'tenant', next));
  };

  const forceRead = async () => {
    setForcing(true);
    setNote(null);
    try {
      // Both legs, because a row is half dispatch and half PostHog and a reader
      // refreshing one would have no way to tell which half moved.
      const outcomes = await Promise.allSettled(
        (data?.activityChecks ?? []).map((id) => api.post(`/checks/${encodeURIComponent(id)}/run`)),
      );
      const refused = outcomes.filter((one) => one.status === 'rejected');
      // The floor refusing is the ordinary case, not an error: it means the
      // numbers on screen are already recent -- read inside a quarter of the
      // check's interval, five minutes for dispatch's twenty.
      setNote(
        refused.length === outcomes.length
          ? 'Already read in the last few minutes — these numbers are current.'
          : 'Reading now; the figures update within a few seconds.',
      );
      setTimeout(() => reload(), 3000);
    } finally {
      setForcing(false);
    }
  };

  if (loading) return <div className="wt-empty">Loading…</div>;
  if (!data) return <LoadIssue error={error} updatedAt={null} what="tenants" onRetry={() => void reload()} />;
  const tenants = data?.tenants ?? [];

  if (!tenants.length) {
    return (
      <div className="wt-empty">
        <div className="big">No tenants mirrored yet.</div>
        <div>The tenant mirror fills once a check reads public.tenants from either database.</div>
      </div>
    );
  }

  // Each leg's own age, rather than one "as of" for two checks on two cadences
  // — the PostHog half reads every ten minutes and dispatch every twenty.
  const runs = data?.activityRuns ?? {};
  const observed = Object.values(data?.activityObservedAt ?? {}).filter(Boolean) as string[];
  const oldest = observed.length ? observed.reduce((a, b) => (a < b ? a : b)) : null;
  const asOf = Object.keys(runs).length
    ? Object.values(runs)
        .map((run) => `${run.leg === 'dispatch' ? 'dispatch' : 'front end'} read ${ago(run.lastObservedAt)}`)
        .join(' · ')
    : `activity as of ${ago(oldest)}`;

  // A link naming a tenant the mirror does not have falls back to the fleet and
  // says so, rather than drawing the fleet under that tenant's name.
  const known = tenants.some((tenant) => tenant.tenantKey === picked);
  const selected = picked && known ? picked : null;

  return (
    <>
      <div className="wt-head">
        <h1>Tenants</h1>
        <span className="sub">
          {tenants.length} known · {asOf}
        </span>
        <span className="wt-spacer" />
        {canRun && (
          <button type="button" className="btn btn-secondary" onClick={() => void forceRead()} disabled={forcing}>
            <RefreshCw size={14} /> {forcing ? 'Reading…' : 'Read now'}
          </button>
        )}
      </div>
      <LoadIssue error={error} updatedAt={updatedAt} what="tenants" onRetry={() => void reload()} />
      {note && <div className="wt-banner">{note}</div>}
      {Object.entries(runs).map(([checkId, run]) => (
        <LegBanner key={checkId} checkId={checkId} run={run} />
      ))}
      {(data?.unattributed?.unmatched_tenant_ids ?? 0) > 0 && (
        <div className="wt-banner wt-banner-warn">
          Some PostHog events carry a <code>tenant_id</code> that matches no tenant here, so that activity is on
          no row below. {data?.fleetSummary}
        </div>
      )}
      {picked && !known && (
        <div className="wt-banner wt-banner-warn">
          No tenant called <strong>{picked}</strong> is mirrored, so this is the whole fleet.
        </div>
      )}
      <div className="wt-table-wrap">
        <table className="wt-table wt-tenants wt-stack">
          <thead>
            <tr>
              <th>Tenant</th>
              <th>Present in</th>
              {/* "Bookings" is audit rows in the Job and Reservation categories —
                  a booking touched, not created. The footnote says so, because a
                  column header cannot. */}
              <th title="Audit rows in the Job and Reservation categories, last hour">Bookings 1h</th>
              <th title="Every other audit row, last hour">Operator 1h</th>
              <th title="Time since the last audit row of any kind">Last action</th>
              <th title="Distinct five-minute buckets with any audit row, last hour">Working</th>
              <th title="People with a front-end event in the last hour (PostHog)">Users</th>
              <th title="Front-end errors in the last hour (PostHog)">FE errors</th>
              <th>Open incidents</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((tenant) => {
              const a: TenantActivity | null = tenant.activity;
              const legs = tenant.legs;
              const never = 'Never attributed — the front end has not tagged an event with this tenant';
              const isPicked = selected === tenant.tenantKey;
              const toggle = () => pick(isPicked ? null : tenant.tenantKey);
              return (
                <tr
                  key={tenant.tenantKey}
                  className={`wt-row-click${isPicked ? ' wt-row-picked' : ''}`}
                  onClick={toggle}
                  tabIndex={0}
                  aria-selected={isPicked}
                  title={isPicked ? 'Back to the whole fleet' : `Draw only ${tenant.tenantKey} below`}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggle();
                    }
                  }}
                >
                  <td className="wt-stack-wide">{tenant.tenantKey}</td>
                  <td className="wt-mono wt-stack-wide" data-label="Present in">
                    {/* A tenant in dispatch but not in the CRM is a real, invisible
                        condition: nobody from that company has ever signed into the
                        CRM, because its local row is created lazily on first request. */}
                    {[tenant.inCrm && 'CRM', tenant.inDispatch && 'dispatch'].filter(Boolean).join(' + ') || 'neither'}
                    {tenant.inDispatch && !tenant.inCrm && (
                      <span className="wt-chip wt-chip-flap" style={{ marginLeft: 8 }}>
                        never used the CRM
                      </span>
                    )}
                  </td>
                  <td data-label="Bookings 1h">
                    <Value value={a?.bookingEvents ?? null} leg={legs?.dispatch} />
                    {a?.auditRowsScanned != null && a.auditRowsScanned >= 5000 && (
                      <span className="wt-dim" title="The bounded scan saturated, so this is a floor">
                        +
                      </span>
                    )}
                  </td>
                  <td data-label="Operator 1h">
                    <Value value={a?.operatorEvents ?? null} leg={legs?.dispatch} />
                  </td>
                  <td data-label="Last action">
                    <Quiet minutes={a?.minutesSinceLastEvent ?? null} leg={legs?.dispatch} />
                  </td>
                  <td data-label="Working">
                    <Value value={a?.activeMinutes ?? null} suffix="m" leg={legs?.dispatch} />
                  </td>
                  <td data-label="Users">
                    <Value value={a?.frontendActiveUsers ?? null} title={never} leg={legs?.frontend} />
                  </td>
                  <td data-label="FE errors">
                    <Value value={a?.frontendErrors ?? null} title={never} leg={legs?.frontend} />
                  </td>
                  <td className="wt-stack-wide" data-label="Open incidents">
                    {tenant.openIncidents.length === 0 ? (
                      <span className="wt-mono">—</span>
                    ) : (
                      tenant.openIncidents.map((incident, index) => (
                        <span key={`${incident.system}-${incident.severity}-${index}`} className="wt-chip">
                          {incident.system} · {incident.severity}
                        </span>
                      ))
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="wt-dim" style={{ marginTop: 12, maxWidth: 780 }}>
        “Bookings” is audit rows in the Job and Reservation categories. dispatch has no indexed timestamp on{' '}
        <code>job</code>, so counting creations off it would cost a sequential scan of its largest table — this is
        what can be counted cheaply, and it counts edits and cancellations as well as creations. “Working” is
        distinct five-minute buckets in which somebody did something. A dash is not a zero: it means no check is
        currently reporting that number. A grey figure is the last one observed, not a current one — hover it for
        when, and why.
        {data?.unattributed?.unattributed_events != null && (
          <>
            {' '}
            {data.unattributed.unattributed_events.toLocaleString()} front-end event(s) in the last hour carried no
            tenant at all, so they appear against nobody here.
          </>
        )}
      </p>

      <div className="wt-daygroup">Activity over time</div>
      <div className="wt-filters">
        <label htmlFor="tenant-pick" className="wt-dim">
          Showing
        </label>
        {/* A plain select, like the Audit screen's filters, rather than a fourth
            drill-down idiom next to those, the Queues drawer and Audit's rows. */}
        <select
          id="tenant-pick"
          className="select"
          value={selected ?? ''}
          onChange={(event) => pick(event.target.value || null)}
        >
          <option value="">All tenants</option>
          {tenants.map((tenant) => (
            <option key={tenant.tenantKey} value={tenant.tenantKey}>
              {tenant.tenantKey}
            </option>
          ))}
        </select>
        <span className="wt-dim" style={{ fontSize: 12 }}>
          {selected
            ? 'One tenant, filtered from data already loaded — the table above still shows everyone.'
            : 'One line per tenant. Click a row, or pick one, to draw it on its own.'}
        </span>
      </div>

      <BoardToolbar board={board} />

      {board.error ? (
        <div className="wt-banner wt-banner-danger">{board.error.message}</div>
      ) : (
        <ChartGrid
          board={board}
          keys={panelsFor(selected)}
          filterSeries={matchesTenant(selected)}
          empty={
            <div className="wt-empty">
              <div className="big">Nothing recorded for {selected} in this window.</div>
              <div>
                Both checks publish a series per tenant only while they are observing one. A tenant with no line
                here is one neither dispatch's audit log nor PostHog has reported on — which is the row above
                showing dashes, seen over time.
              </div>
            </div>
          }
        />
      )}
    </>
  );
}
